import React, { useState, useEffect } from 'react';
import { Card, Typography, Progress, Select, Button, Space, Divider, Spin, Alert } from 'antd';
import { 
  TrophyOutlined, 
  CalendarOutlined, 
  DollarOutlined,
  ReloadOutlined,
  SettingOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useDealStore from '../store/dealStore';
import useTargetStore from '../store/targetStore';
import useSettingsStore from '../store/settingsStore';

const { Title, Text } = Typography;
const { Option } = Select;

const TargetSidebar = () => {
  const navigate = useNavigate();
  const { deals, fetchDeals, stats: dealStats, fetchDealStats } = useDealStore();
  const { 
    targetHierarchy, 
    isLoading: targetLoading, 
    error: targetError,
    fetchTargetHierarchy,
    refreshAchievements 
  } = useTargetStore();
  const { formatCurrency } = useSettingsStore();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Fetch target data on mount and when year changes
  useEffect(() => {
    fetchTargetHierarchy(selectedYear);
    fetchDeals();
    fetchDealStats();
  }, [selectedYear, fetchTargetHierarchy, fetchDeals, fetchDealStats]);

  // Auto-refresh target data every 30 seconds to catch updates from deal changes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTargetHierarchy(selectedYear);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [selectedYear, fetchTargetHierarchy]);

  // Refresh when page becomes visible (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchTargetHierarchy(selectedYear);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedYear, fetchTargetHierarchy]);

  const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);
  
  // Get current quarter data
  const getCurrentQuarterData = () => {
    if (!targetHierarchy || !targetHierarchy.quarters) return null;
    return targetHierarchy.quarters.find(q => q.period === `Q${currentQuarter}`);
  };

  const currentQuarterData = getCurrentQuarterData();
  const annualTarget = targetHierarchy?.annual;
  
  // Calculate overall progress
  const overallProgress = annualTarget && annualTarget.targetAmount > 0 
    ? (annualTarget.achievedAmount / annualTarget.targetAmount) * 100 
    : 0;

  // Calculate current quarter progress
  const currentQuarterProgress = currentQuarterData && currentQuarterData.targetAmount > 0
    ? (currentQuarterData.achievedAmount / currentQuarterData.targetAmount) * 100
    : 0;

  const handleRefreshData = async () => {
    await refreshAchievements(selectedYear);
    await fetchTargetHierarchy(selectedYear);
    await fetchDeals();
    await fetchDealStats();
  };

  return (
    <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <TrophyOutlined className="text-blue-500" />
            <Title level={4} className="mb-0">Targets</Title>
          </div>
          <Button
            type="text"
            icon={<SettingOutlined />}
            size="small"
            onClick={() => navigate('/admin/settings')}
            className="text-gray-500 hover:text-blue-500"
          />
        </div>
        
        <Select
          value={selectedYear}
          onChange={setSelectedYear}
          className="w-full"
          suffixIcon={<CalendarOutlined />}
        >
          {[2023, 2024, 2025, 2026].map(year => (
            <Option key={year} value={year}>{year}</Option>
          ))}
        </Select>
      </div>

      <div className="p-4 space-y-4">
        {targetLoading ? (
          <div className="text-center py-8">
            <Spin size="large" />
            <Text className="block mt-2 text-gray-500">Loading targets...</Text>
          </div>
        ) : targetError ? (
          <Alert
            message="Error Loading Targets"
            description={targetError}
            type="error"
            showIcon
            className="mb-4"
          />
        ) : (
          <>
            {/* Overall Achievement */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <div className="text-center mb-3">
                <div className="mb-2">
                  <Text className="text-xs text-gray-500 dark:text-gray-400 block">Remaining Target</Text>
                  <Text className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {formatCurrency((annualTarget?.targetAmount || 0) - (annualTarget?.achievedAmount || 0))}
                  </Text>
                </div>
                <div className="flex justify-center items-center gap-2 text-sm">
                  <div>
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">Achieved</Text>
                    <Text className="font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(annualTarget?.achievedAmount || 0)}
                    </Text>
                  </div>
                  <Text className="text-gray-400">/</Text>
                  <div>
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">Target</Text>
                    <Text className="font-semibold">
                      {formatCurrency(annualTarget?.targetAmount || 0)}
                    </Text>
                  </div>
                </div>
              </div>
              <Progress
                percent={overallProgress}
                strokeColor="#52c41a"
                trailColor="#e6f7ff"
                className="mb-2"
              />
              <Text className="text-sm text-center block text-gray-600 dark:text-gray-400">
                {overallProgress.toFixed(1)}% Achieved
              </Text>
            </div>

            {/* Annual Target */}
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Text className="font-medium">Annual Target</Text>
                <TrophyOutlined className="text-yellow-500" />
              </div>
              <Text className="text-lg font-bold">
                {formatCurrency(annualTarget?.targetAmount || 0)}
              </Text>
            </div>

            <Divider className="my-3" />

            {/* Current Quarter Only */}
            <div>
              <Text className="font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                Current Quarter
              </Text>
              {currentQuarterData ? (
                <div className="p-3 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <CalendarOutlined className="text-blue-500" />
                      <Text className="font-medium text-blue-600 dark:text-blue-400">
                        Q{currentQuarter}
                      </Text>
                    </div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  </div>
                  <div className="mb-2">
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">Target</Text>
                    <Text className="text-sm font-semibold block">
                      {formatCurrency(currentQuarterData.targetAmount)}
                    </Text>
                  </div>
                  <div className="mb-2">
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">Remaining</Text>
                    <Text className="text-sm font-bold text-orange-600 dark:text-orange-400 block">
                      {formatCurrency(currentQuarterData.targetAmount - currentQuarterData.achievedAmount)}
                    </Text>
                  </div>
                  <Progress
                    percent={currentQuarterProgress}
                    size="small"
                    strokeColor="#52c41a"
                    trailColor="#e6f7ff"
                    showInfo={false}
                  />
                  <div className="flex justify-between mt-1">
                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                      {currentQuarterProgress.toFixed(1)}% achieved
                    </Text>
                    <Text className="text-xs text-green-600 dark:text-green-400 font-semibold">
                      {formatCurrency(currentQuarterData.achievedAmount)}
                    </Text>
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                  <Text className="text-center text-gray-500 dark:text-gray-400">
                    No target set for Q{currentQuarter}
                  </Text>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => navigate('/admin/settings')}
                    className="w-full mt-2"
                  >
                    Set Target
                  </Button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Quick Actions */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <Space direction="vertical" className="w-full">
            <Button
              type="primary"
              icon={<DollarOutlined />}
              onClick={() => navigate('/deals/new')}
              className="w-full"
              size="small"
            >
              Add Deal
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefreshData}
              className="w-full"
              size="small"
              loading={targetLoading}
            >
              Refresh Data
            </Button>
          </Space>
        </div>

        {/* Performance Indicator */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-3 rounded-lg">
          <div className="text-center">
            <Text className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
              Current Performance
            </Text>
            <div className="flex items-center justify-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                overallProgress >= 75 ? 'bg-green-500' :
                overallProgress >= 50 ? 'bg-yellow-500' :
                overallProgress >= 25 ? 'bg-orange-500' : 'bg-red-500'
              }`} />
              <Text className="text-sm font-medium">
                {overallProgress >= 75 ? 'Excellent' :
                 overallProgress >= 50 ? 'Good' :
                 overallProgress >= 25 ? 'Fair' : 'Needs Attention'}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetSidebar;
